import { AutocompleteWithLabel } from '@adotel/components'
import {
  LazyQueryExecFunction,
  OperationVariables,
  useLazyQuery,
  useQuery
} from '@apollo/client'
import { ButtonBase, Grow, Typography } from '@material-ui/core'
import React, { useEffect } from 'react'

import { ACCOMMODATIONS_QUERY } from '../../../graphql/accommodation'
import { CHANNELS_QUERY } from '../../../graphql/channel'
import { ROOMTYPES_QUERY } from '../../../graphql/roomtype'
import { DateObject } from '../../../helpers/date'
import { getTranslate } from '../../../localization'
import { Accommodation } from '../../../types/accommodation'
import { Channel } from '../../../types/channel'
import { AllocatedData } from '../../../types/inventoryAllocatedList'
import { RoomTypeBrief } from '../../../types/roomtype'
import useStyles from './styles/PrimaryFilterModal.styles'

interface FilterModalProps {
  setFilterButtonSelected: React.Dispatch<React.SetStateAction<boolean>>
  selectedAccommodation: any
  setselectedAccommodation: React.Dispatch<
    React.SetStateAction<
      | {
          label: string
          value: string
          code: string
        }
      | undefined
    >
  >
  selectedRoomTypeCode: any
  setselectedRoomTypeCode: (v: {
    label: string
    value: string
    code: string
  }) => void
  selectedChannel: any
  setselectedChannel: (v: {
    label: string
    value: string
    code: string
  }) => void
  getAllocatedList: LazyQueryExecFunction<
    {
      allocatedList: AllocatedData
    },
    OperationVariables
  >
  dateObject: DateObject
}

const FilterModal = ({
  setFilterButtonSelected,
  selectedAccommodation,
  setselectedAccommodation,
  selectedRoomTypeCode,
  setselectedRoomTypeCode,
  selectedChannel,
  setselectedChannel,
  getAllocatedList,
  dateObject
}: FilterModalProps) => {
  const classes = useStyles()

  const { data: accommodationsData, loading: loadingForAccommodationList } =
    useQuery<{
      accommodations: Accommodation[]
    }>(ACCOMMODATIONS_QUERY, { notifyOnNetworkStatusChange: true })
  const [getRoomTypes, { data: roomtypes, loading: loadingForRoomtypes }] =
    useLazyQuery<{
      roomTypes: RoomTypeBrief[]
    }>(ROOMTYPES_QUERY)
  const { data: channelsData, loading: loadingForChannel } = useQuery<{
    channels: Channel[]
  }>(CHANNELS_QUERY)
  useEffect(() => {
    if (selectedAccommodation) {
      getRoomTypes({
        variables: {
          propertyCode: selectedAccommodation.code,
          showDeletedRoomTypes: false,
          showDeactivedRoomTypes: false
        }
      })
    }
  }, [selectedAccommodation])

  return (
    <Grow in>
      <div className={classes.root}>
        <div className={classes.selectBoxes}>
          <div className={classes.oneBoxInRow}>
            <div>
              <div className={classes.ButtonsContainer}>
                <ButtonBase
                  onClick={() => {
                    setFilterButtonSelected(false)
                  }}>
                  <Typography
                    className={classes.typographyOfTitle}
                    variant="h3"
                    color="primary">
                    {getTranslate('Cancel')}
                  </Typography>{' '}
                </ButtonBase>
                <div className={classes.titleContainer}>
                  <Typography
                    className={classes.typographyOfTitle}
                    variant="h1">
                    {getTranslate('Filter')}
                  </Typography>
                </div>

                <ButtonBase
                  onClick={() => {
                    setFilterButtonSelected(false)
                    if (selectedAccommodation && selectedAccommodation.value) {
                      getAllocatedList({
                        variables: {
                          propertyCode:
                            selectedAccommodation && selectedAccommodation.code,
                          roomTypeCode:
                            selectedRoomTypeCode && !!selectedRoomTypeCode.code
                              ? selectedRoomTypeCode.code
                              : '',
                          channelID:
                            selectedChannel && !!selectedChannel.value
                              ? selectedChannel.value
                              : '',
                          from:
                            dateObject && !!dateObject.gregorianStartDate
                              ? dateObject.gregorianStartDate
                              : '',
                          to:
                            dateObject && !!dateObject.gregorianEndDate
                              ? dateObject.gregorianEndDate
                              : ''
                        }
                      })
                    }
                  }}>
                  <Typography
                    className={classes.typographyOfTitle}
                    variant="h3"
                    color="primary">
                    {getTranslate('Done')}
                  </Typography>{' '}
                </ButtonBase>
              </div>

              <AutocompleteWithLabel
                label={getTranslate('Accommodation')}
                placeholder={getTranslate('Select accommodation')}
                includeInputInList
                onChange={(event, v: any) => {
                  if (v) {
                    setselectedAccommodation({
                      value: v.value,
                      label: v.label,
                      code: v.code
                    })
                  } else {
                    setselectedAccommodation({
                      value: '',
                      label: '',
                      code: ''
                    })
                  }
                  setselectedRoomTypeCode({
                    value: '',
                    label: '',
                    code: ''
                  })
                }}
                fullWidth
                getOptionSelected={(option, value) =>
                  option.value === value.value && option.label === value.label
                }
                getOptionLabel={(option) => option.label || ''}
                options={
                  accommodationsData?.accommodations.map((item) => ({
                    label: item.basicDetails.name,
                    value: item.basicDetails.accommodationID || '',
                    code: item.basicDetails.propertyCode
                  })) || []
                }
                value={selectedAccommodation}
                renderOption={(option) => <>{option.label}</>}
                size="small"
                inputSize="small"
                loading={loadingForAccommodationList}
                loadingText={getTranslate('Loading') + '...'}
              />
              <AutocompleteWithLabel
                label={getTranslate('Room Type')}
                placeholder={getTranslate('Select Room type')}
                includeInputInList
                onChange={(event, v: any) => {
                  if (v) {
                    setselectedRoomTypeCode({
                      value: v.value,
                      label: v.label,
                      code: v.code
                    })
                  } else {
                    setselectedRoomTypeCode({
                      value: '',
                      label: '',
                      code: ''
                    })
                  }
                }}
                getOptionSelected={(option, value) =>
                  option.value === value.value && option.label === value.label
                }
                getOptionLabel={(option) => option.label || ''}
                value={selectedRoomTypeCode}
                options={
                  roomtypes?.roomTypes.map((roomtype) => ({
                    label: roomtype.name,
                    value: roomtype.id || '',
                    code: roomtype.displayRoomTypeCode
                  })) || []
                }
                renderOption={(option) => <>{option.label}</>}
                size="small"
                fullWidth
                inputSize="small"
                disabled={!!!selectedAccommodation?.value}
                loading={loadingForRoomtypes}
                loadingText={getTranslate('Loading') + '...'}
              />
              <AutocompleteWithLabel
                label={getTranslate('Channel')}
                placeholder={getTranslate('Select Channel')}
                includeInputInList
                onChange={(event, v: any) => {
                  if (v) {
                    setselectedChannel({
                      value: v.value,
                      label: v.label,
                      code: v.code
                    })
                  } else {
                    setselectedChannel({
                      value: '',
                      label: '',
                      code: ''
                    })
                  }
                }}
                value={selectedChannel}
                getOptionSelected={(option, value) =>
                  option.value === value.value && option.label === value.label
                }
                getOptionLabel={(option) => option.label || ''}
                options={
                  channelsData?.channels.map((item) => ({
                    label: item.basicDetails.name,
                    value: item.basicDetails.channelID || '',
                    code: item.basicDetails.displayChannelCode
                  })) || []
                }
                renderOption={(option) => <>{option.label}</>}
                size="small"
                inputSize="small"
                disabled={!!!selectedAccommodation?.value}
                loading={loadingForChannel}
                loadingText={getTranslate('Loading') + '...'}
              />
            </div>
          </div>
        </div>
      </div>
    </Grow>
  )
}

export default FilterModal
